import { StringData } from "../../core/StringData";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { isCoarsePointer } from "../../utils/isCoarsePointer";
import { styleTxn } from "../../utils/style-txn";
import { CursorReactiveModule } from "./CursorReactiveModule";

const TAU = Math.PI * 2;
const RAD2DEG = 180 / Math.PI;
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

const shortestArc = (from: number, to: number) => {
  let d = (to - from) % TAU;
  if (d > Math.PI) d -= TAU;
  if (d < -Math.PI) d += TAU;
  return from + d;
};

type SpotlightState = {
  angle: number;
  dist: number;
  tAngle: number;
  tDist: number;
  prevDeg: number;
  prevDist: number;
};

const spotState = new WeakMap<StringObject, SpotlightState>();
const getSpot = (o: StringObject): SpotlightState => {
  let s = spotState.get(o);
  if (!s) {
    s = { angle: 0, dist: 0, tAngle: 0, tDist: 0, prevDeg: NaN, prevDist: NaN };
    spotState.set(o, s);
  }
  return s;
};

export class StringSpotlight extends CursorReactiveModule {
  constructor(context: any) {
    super(context);
    this.htmlKey = "spotlight";

    this.nearOnly = false;
    this.useAllObjects = false;
    this.maxDistanceMultiplier = 1;

    this.attributesToMap.push(
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (v: number) =>
          clamp01(
            this.tools.adaptiveLerp.process({
              value: v,
              inMin: 0.1,
              inMax: 1.0,
              outMin: 0.05,
              outMax: 0.65,
            })
          ),
      },
      { key: "angle-threshold", type: "number", fallback: 0.2 },
      { key: "distance-threshold", type: "number", fallback: 0.5 },
      { key: "deadzone", type: "number", fallback: 4 },
      { key: "dist-max", type: "number", fallback: 0 }
    );
  }

  initializeObject(
    id: number,
    obj: StringObject,
    el: HTMLElement,
    attrs: Record<string, any>
  ): void {
    super.initializeObject(id, obj, el, attrs);

    obj.setProperty("spotlight-angle-rad", 0);
    obj.setProperty("spotlight-distance", 0);
    obj.setProperty("spotlight-angle-rad-target", 0);
    obj.setProperty("spotlight-distance-target", 0);

    frameDOM.measure(() => {
      this.refreshPointerState(obj, /*allowFallback=*/ true);
      frameDOM.mutate(() => {
        styleTxn.run(() => {
          this.updateSpotlightState(obj, {
            forceImmediate: true,
            forceEmit: true,
            bypassDeadzone: true,
          });
        });
      });
    });
  }

  override onMutate(data: StringData): void {
    if (this.enabled) {
      super.onMutate(data);
      const targets = this.getCursorTargets(/*allowFallback=*/ false);
      const list = targets.length > 0 ? targets : this.getCursorTargets(/*allowFallback=*/ true);
      list.forEach((object) => {
        this.updateSpotlightState(object);
      });
    }
  }

  protected onCursorScrollUpdate(): void {
    const targets = this.getCursorTargets(/*allowFallback=*/ false);
    const list = targets.length > 0 ? targets : this.getCursorTargets(/*allowFallback=*/ true);

    list.forEach((object) => {
      this.updateSpotlightState(object, { bypassDeadzone: true });
    });
  }

  private updateSpotlightState(
    object: StringObject,
    options: { forceImmediate?: boolean; forceEmit?: boolean; bypassDeadzone?: boolean } = {}
  ): void {
    const dx = object.getProperty<number>("dx");
    const dy = object.getProperty<number>("dy");
    const dist = object.getProperty<number>("dist");

    if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(dist)) {
      return;
    }

    const s = getSpot(object);

    const DEADZONE = object.getProperty<number>("deadzone") ?? 4;
    if (options.bypassDeadzone || dist! > DEADZONE) {
      s.tAngle = Math.atan2(dy!, dx!);
    }

    const distMax = object.getProperty<number>("dist-max") ?? 0;
    s.tDist = distMax > 0 ? Math.min(dist!, distMax) : dist!;

    object.setProperty("spotlight-distance-target", s.tDist);
    object.setProperty("spotlight-angle-rad-target", s.tAngle);

    if (options.forceImmediate) {
      s.angle = s.tAngle;
      s.dist = s.tDist;
    } else {
      const lerp = clamp01(object.getProperty<number>("lerp") ?? 0.15);
      const nextA = shortestArc(s.angle, s.tAngle);
      s.angle += (nextA - s.angle) * lerp;
      s.dist += (s.tDist - s.dist) * lerp;
    }

    object.setProperty("spotlight-angle-rad", s.angle);
    object.setProperty("spotlight-distance", s.dist);

    const degRaw = s.angle * RAD2DEG - 90;

    const angleEps = object.getProperty<number>("angle-threshold") ?? 0.2;
    const distEps = object.getProperty<number>("distance-threshold") ?? 0.5;

    const angleChanged =
      options.forceEmit || Number.isNaN(s.prevDeg) || Math.abs(degRaw - s.prevDeg) > angleEps;
    const distChanged =
      options.forceEmit || Number.isNaN(s.prevDist) || Math.abs(s.dist - s.prevDist) > distEps;

    if (angleChanged || distChanged) {
      const cssDeg = Math.round(degRaw * 10) / 10;
      const cssDist = Math.round(s.dist * 10) / 10;

      this.applyToElementAndConnects(object, (el) => {
        styleTxn.setVars(el, {
          "--spotlight-angle": String(cssDeg),
          "--spotlight-angle-deg": `${cssDeg}deg`,
          "--spotlight-distance": String(cssDist),
        });
      });

      s.prevDeg = degRaw;
      s.prevDist = s.dist;

      this.events.emit(`spotlight:update:${object.id}`, {
        distance: cssDist,
        angleDeg: cssDeg,
      });
    }
  }
}
